import React from 'react';
import { ConviteDetalhe, DisparoResumido } from '../../../interfaces/PortalCotacoes';
import './EnvioPorCanal.css';

/**
 * O desfecho REAL do envio, por canal — at-07 (19/08/2026).
 *
 * ## O defeito que este componente corrige
 *
 * A célula mostrava `convite.canais` ("email, whatsapp"), que é **configuração**, não resultado.
 * E o `convite.status` vira `ENVIADO` **antes** de os disparos rodarem, sem nada revertê-lo em
 * falha — então, com o SMTP fora do ar, a linha do fornecedor exibia "Enviado" e o e-mail nunca
 * saíra. Falso sucesso (classe de defeito da at-60) na tela principal do módulo.
 *
 * ## Três estados, e por que são três
 *
 * - **enviado** — o servidor entregou à plataforma (e-mail, ou WhatsApp via OctaDesk).
 * - **falhou** — tentou e não conseguiu; o motivo vai no `title`.
 * - **a enviar** — o portal só preparou o link (`manual: true`, o `wa.me`): ninguém enviou nada
 *   ainda, e chamar isso de "enviado" seria repetir o defeito por outro caminho. É o mesmo
 *   cuidado que a Auditoria já tem no título do evento desde 14/08/2026.
 *
 * ⚠️ **Uma linha, sempre** (regra do Victor, 12/08/2026): os canais ficam lado a lado com
 * `nowrap` + reticências, e o `title` da célula carrega o detalhe completo (motivo do erro e
 * horário). Nunca empilhar um canal por linha.
 */

const ROTULO_CANAL: Record<string, string> = { EMAIL: 'E-mail', WHATSAPP: 'WhatsApp' };

function estado(d: DisparoResumido): { texto: string; classe: string } {
  if (!d.sucesso) return { texto: 'falhou', classe: 'envio-canal--falhou' };
  if (d.manual) return { texto: 'a enviar', classe: 'envio-canal--manual' };
  return { texto: 'enviado', classe: 'envio-canal--ok' };
}

function detalhe(d: DisparoResumido): string {
  const rotulo = ROTULO_CANAL[d.canal] ?? d.canal;
  const quando = new Date(d.em).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  if (!d.sucesso) return `${rotulo}: falhou em ${quando} — ${d.erro ?? 'motivo não informado'}`;
  if (d.manual) {
    return (
      `${rotulo}: link preparado em ${quando}. O sistema não envia esta mensagem — ` +
      'quem envia é você, pela ação de WhatsApp no botão "Ações".'
    );
  }
  return `${rotulo}: enviado em ${quando}${
    d.provedorMensagemId ? ` (mensagem ${d.provedorMensagemId})` : ''
  }`;
}

const EnvioPorCanal: React.FC<{ convite: ConviteDetalhe }> = ({ convite }) => {
  const disparos = convite.ultimosDisparos ?? [];

  /*
   * Nenhum disparo registrado: a cotação foi enviada mas este convite não teve tentativa em
   * canal nenhum (ou é anterior à at-07). Mostra os canais CONFIGURADOS, deixando claro que é
   * expectativa e não resultado — dizer "—" esconderia que existe canal previsto.
   */
  if (disparos.length === 0) {
    const previstos = convite.canais.map((c) => ROTULO_CANAL[c] ?? c).join(', ');
    /*
     * Texto CURTO de propósito: a versão longa ("E-mail, WhatsApp: sem registro") era cortada
     * pela largura da coluna e chegava ao usuário como "E-mail, WhatsAp…" — indistinguível da
     * antiga coluna "Canal", ou seja, a reticência engolia justamente a informação nova. Os
     * canais previstos vivem no `title`, onde há espaço.
     */
    return (
      <span
        className="Data envio-canais envio-canal--sem-registro"
        title={`Sem registro de envio. Canais previstos: ${previstos || 'nenhum'}.`}
      >
        sem registro
      </span>
    );
  }

  const titulo = disparos.map(detalhe).join(' | ');

  return (
    <span className="Data envio-canais" title={titulo}>
      {disparos.map((d, i) => {
        const { texto, classe } = estado(d);
        return (
          <span key={d.canal}>
            {i > 0 && <span className="envio-canal-separador"> · </span>}
            <span className="envio-canal-nome">{ROTULO_CANAL[d.canal] ?? d.canal}</span>{' '}
            <span className={`envio-canal-estado ${classe}`}>{texto}</span>
          </span>
        );
      })}
    </span>
  );
};

export default EnvioPorCanal;
